import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import api from '../utils/api';
import axios from 'axios';

// 타입 정의
// interface User {
//   id: string;
//   username: string;
//   role: string;
//   school?: string;
// }

interface AuthContextType {
  user: any | null;
  loading: boolean;
  error: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  signup: (username: string, password: string, school: string) => Promise<void>;
}

// 기본값으로 컨텍스트 생성
const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: false,
  error: null,
  login: async () => {},
  logout: async () => {},
  checkAuth: async () => {},
  signup: async () => {},
});

// 컨텍스트 훅
export const useAuth = () => useContext(AuthContext);

// 프로바이더 컴포넌트
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // 컴포넌트 마운트 시 인증 상태 확인
  useEffect(() => {
    checkAuth();
  }, []);

  // 인증 상태 확인 함수
  const checkAuth = async () => {
    try {
      setLoading(true);
      const response = await api.get('/auth/profile');

      let userData;
      if (response.data.result) {
        userData = response.data.result;
      } else {
        userData = response.data;
      }

      // 사용자 역할이 있는지 확인하고 기본값 설정
      if (!userData.role) {
        console.warn('User role is missing in the response');
        userData.role = 'STUDENT'; // 기본값 설정
      }

      setUser(userData);
      setError(null);
    } catch (err) {
      console.error('Auth check error:', err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // 로그인 함수
  const login = async (username: string, password: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.post('/auth/login', { username, password });

      // 응답 데이터 구조 확인
      let userData;
      if (response.data.result) {
        userData = response.data.result;
      } else {
        userData = response.data;
      }

      setUser(userData);

      // 추가 프로필 정보 가져오기
      try {
        const profileResponse = await api.get('/auth/profile');

        let completeUserData;
        if (profileResponse.data.result) {
          completeUserData = profileResponse.data.result;
        } else {
          completeUserData = profileResponse.data;
        }

        // 완전한 사용자 정보로 업데이트
        setUser(completeUserData);
      } catch (profileErr) {
        console.error('Failed to fetch complete profile after login:', profileErr);
        // 프로필 정보 가져오기 실패 시에도 기본 로그인은 유지
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        setError(err.response.data.message || '로그인에 실패했습니다.');
      } else {
        setError('서버 연결에 문제가 있습니다.');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // 회원가입 함수
  const signup = async (username: string, password: string, school: string) => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.post('/auth/signup', {
        username,
        password,
        school
      });

      // 성공적인 응답이라면 응답 데이터 반환
      return response.data;
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        const errorMessage = err.response.data.message || '회원가입에 실패했습니다.';
        setError(errorMessage);
        throw new Error(errorMessage);
      } else {
        setError('서버 연결에 문제가 있습니다.');
        throw new Error('서버 연결에 문제가 있습니다.');
      }
    } finally {
      setLoading(false);
    }
  };

  // 로그아웃 함수
  const logout = async () => {
    try {
      setLoading(true);
      await api.post('/auth/logout');
      setUser(null);
    } catch (err) {
      console.error('로그아웃 오류:', err);
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    checkAuth,
    signup
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
